import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { IconRefresh, IconDiamond, IconShare, IconTrash, IconCheck, IconSparkles } from '@tabler/icons-react';
import { functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import ProgressMap from './ProgressMap';
import { useAdminView } from '../contexts/AdminViewContext';
import { resolvePostMedia } from '../utils/postMedia';
import useVideoProgressBar from '../hooks/useVideoProgressBar';
import MediaSelectionTiles from './MediaSelectionTiles';
import VideoSlideOverlay from './VideoSlideOverlay';
import './ManagePostCard.css';

const DEFAULT_STRAVA_SHARE_FLOW_MODE = 'admin_media_upload';
const STRAVA_SHARE_FLOW_MODES = new Set([
  'manual_only',
  'admin_media_upload',
  'all_media_upload'
]);

const DEFAULT_2D_MODEL_ETA_SECONDS = 120;
const DEFAULT_3D_MODEL_ETA_SECONDS = 30;
const DEFAULT_3D_RENDERER_ETA_SECONDS = 30;
const DEFAULT_3D_RENDERER_UPLOAD_ETA_SECONDS = 5;
const MIN_3D_RENDERER_IN_PROGRESS_REMAINING_SECONDS = 5;
const STRAVA_SHARE_SELECTION_CACHE_VERSION = 1;
const DEFAULT_MEDIA_SELECTION = Object.freeze({
  generated: true,
  generated2d: true,
  video: true,
  original: true
});

function getStravaShareSelectionCacheKey(userId) {
  return `mythicmaps:stravaShareSelection:${userId}:v${STRAVA_SHARE_SELECTION_CACHE_VERSION}`;
}

function normalizeMediaSelection(value, fallback = DEFAULT_MEDIA_SELECTION) {
  const source = value && typeof value === 'object' ? value : {};
  return {
    generated: typeof source.generated === 'boolean' ? source.generated : fallback.generated,
    generated2d: typeof source.generated2d === 'boolean' ? source.generated2d : fallback.generated2d,
    video: typeof source.video === 'boolean' ? source.video : fallback.video,
    original: typeof source.original === 'boolean' ? source.original : fallback.original,
  };
}

function readCachedStravaShareSelection(userId) {
  if (!userId || typeof window === 'undefined') {
    return null;
  }

  try {
    const storage = window.localStorage;
    if (!storage) return null;

    const raw = storage.getItem(getStravaShareSelectionCacheKey(userId));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (parsed?.version !== STRAVA_SHARE_SELECTION_CACHE_VERSION) {
      return null;
    }

    return normalizeMediaSelection(parsed.selection);
  } catch (error) {
    console.warn('ManagePostCard: failed to read cached Strava share selection', error);
    return null;
  }
}

function writeCachedStravaShareSelection(userId, selection) {
  if (!userId || typeof window === 'undefined') {
    return;
  }

  try {
    const storage = window.localStorage;
    if (!storage) return;

    storage.setItem(
      getStravaShareSelectionCacheKey(userId),
      JSON.stringify({
        version: STRAVA_SHARE_SELECTION_CACHE_VERSION,
        selection: normalizeMediaSelection(selection),
      })
    );
  } catch (error) {
    console.warn('ManagePostCard: failed to cache Strava share selection', error);
  }
}

function applyMediaAvailability(selection, availability) {
  return {
    generated: Boolean(availability.generated) && Boolean(selection.generated),
    generated2d: Boolean(availability.generated2d) && Boolean(selection.generated2d),
    video: Boolean(availability.video) && Boolean(selection.video),
    original: Boolean(availability.original) && Boolean(selection.original),
  };
}

function normalizeStravaShareFlowMode(value) {
  if (STRAVA_SHARE_FLOW_MODES.has(value)) {
    return value;
  }

  return DEFAULT_STRAVA_SHARE_FLOW_MODE;
}

function toDateValue(value) {
  if (!value) {
    return null;
  }

  if (typeof value.toDate === 'function') {
    return value.toDate();
  }

  const dateValue = value instanceof Date ? value : new Date(value);
  return Number.isNaN(dateValue.getTime()) ? null : dateValue;
}

function toFiniteSeconds(value, divisor = 1) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return numericValue / divisor;
}

function calculateEtaBreakdown({
  requestData,
  generationTimes,
  threeDRendererTimingStats,
  renderer3DRenderCacheHit,
  renderer3DRenderCacheStatus,
  rendererEtaCalibration,
  nowMs = Date.now(),
}) {
  if (!requestData?.generationStartTime) {
    return null;
  }

  const startTime = toDateValue(requestData.generationStartTime);
  if (!startTime) {
    return null;
  }

  const elapsedSeconds = Math.max(0, (nowMs - startTime.getTime()) / 1000);
  const model = requestData.generationModel || 'gemini-3-pro';
  const is3DProcessing =
    requestData.use3DMap === true ||
    requestData.mapSource === '3d-capture' ||
    requestData.mapSource === '3d-renderer';
  const isRendererFlow = requestData.threeDRenderSource === 'renderer';

  const modelAverageSeconds = requestData.__testDurationSeconds ?
    requestData.__testDurationSeconds :
    (generationTimes?.[model]?.averageDurationSeconds || (is3DProcessing ? DEFAULT_3D_MODEL_ETA_SECONDS : DEFAULT_2D_MODEL_ETA_SECONDS));

  const rendererAverageSeconds = threeDRendererTimingStats?.averageDurationSeconds || DEFAULT_3D_RENDERER_ETA_SECONDS;
  const rendererDelaySeconds = toFiniteSeconds(requestData.renderer3DDelayTime, 1000);
  const rendererExecutionSeconds = toFiniteSeconds(requestData.renderer3DExecutionTime, 1000);
  const rendererRecordedDurationSeconds = toFiniteSeconds(requestData.renderer3DRenderDurationSeconds);
  const calibratedDelaySeconds = rendererEtaCalibration?.delaySeconds ?? rendererDelaySeconds;
  const calibratedExecutionSeconds = rendererEtaCalibration?.executionSeconds ?? rendererExecutionSeconds;
  const calibratedObservedAtMs = Number.isFinite(rendererEtaCalibration?.observedAtMs) ?
    rendererEtaCalibration.observedAtMs :
    null;

  const cacheReady = renderer3DRenderCacheHit || renderer3DRenderCacheStatus === 'COMPLETED';
  const status = requestData.status || 'unknown';

  let rendererRemainingSeconds = 0;
  let rendererEtaSource = 'not-applicable';

  if (isRendererFlow && !cacheReady) {
    switch (status) {
      case 'renderer_3d_starting':
      case 'renderer_3d_queued':
      case 'geodata_created':
        rendererRemainingSeconds = rendererAverageSeconds;
        rendererEtaSource = 'renderer-average-while-queued';
        break;
      case 'renderer_3d_in_progress': {
        const observedRendererElapsedSeconds = Math.max(0, (calibratedDelaySeconds || 0) + (calibratedExecutionSeconds || 0));
        const localRendererProgressSeconds = calibratedObservedAtMs !== null ?
          Math.max(0, (nowMs - calibratedObservedAtMs) / 1000) :
          0;
        const knownRendererElapsedSeconds = observedRendererElapsedSeconds + localRendererProgressSeconds;
        rendererRemainingSeconds = Math.max(
          MIN_3D_RENDERER_IN_PROGRESS_REMAINING_SECONDS,
          rendererAverageSeconds - knownRendererElapsedSeconds
        );
        rendererEtaSource = 'renderer-average-minus-observed-delay-execution-plus-local-progress';
        break;
      }
      case 'renderer_3d_uploading':
        rendererRemainingSeconds = DEFAULT_3D_RENDERER_UPLOAD_ETA_SECONDS;
        rendererEtaSource = 'renderer-upload-tail';
        break;
      default:
        rendererRemainingSeconds = 0;
        rendererEtaSource = 'renderer-finished-or-not-started';
        break;
    }
  }

  let modelRemainingSeconds = modelAverageSeconds;
  let modelEtaSource = 'full-model-average-pending-renderer';

  if (!isRendererFlow) {
    modelRemainingSeconds = Math.max(0, modelAverageSeconds - elapsedSeconds);
    modelEtaSource = 'model-average-minus-total-elapsed';
  } else if (
    status === 'openai_processing' ||
    status === 'openai_processing_complete'
  ) {
    const openAiElapsedSeconds = rendererRecordedDurationSeconds !== null ?
      Math.max(0, elapsedSeconds - rendererRecordedDurationSeconds) :
      elapsedSeconds;
    modelRemainingSeconds = Math.max(0, modelAverageSeconds - openAiElapsedSeconds);
    modelEtaSource = rendererRecordedDurationSeconds !== null ?
      'model-average-minus-elapsed-since-renderer-finished' :
      'model-average-minus-total-elapsed-no-renderer-duration';
  }

  const remainingSeconds = Math.max(0, Math.round(rendererRemainingSeconds + modelRemainingSeconds));
  const baseExpectedSeconds = Math.max(
    1,
    Math.round(modelAverageSeconds + (isRendererFlow && !cacheReady ? rendererAverageSeconds : 0))
  );
  const dynamicExpectedSeconds = Math.max(
    baseExpectedSeconds,
    Math.round(elapsedSeconds + remainingSeconds)
  );

  return {
    status,
    model,
    is3DProcessing,
    isRendererFlow,
    cacheReady,
    renderer3DRenderCacheHit,
    renderer3DRenderCacheStatus,
    elapsedSeconds,
    modelAverageSeconds,
    modelRemainingSeconds,
    modelEtaSource,
    rendererAverageSeconds,
    rendererRemainingSeconds,
    rendererEtaSource,
    rendererDelaySeconds,
    rendererExecutionSeconds,
    calibratedDelaySeconds,
    calibratedExecutionSeconds,
    calibratedObservedAtMs,
    rendererRecordedDurationSeconds,
    baseExpectedSeconds,
    dynamicExpectedSeconds,
    remainingSeconds,
  };
}

const ManagePostCard = ({
  post,
  user,
  onDelete,
  requestData,
  onCreditsAwarded,
  onAutoUploadEnabledChange
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { actualIsAdmin } = useAdminView();

  // Fun AI processing messages - now uses translations
  const getAIMessage = () => {
    const messages = [
      t('share.aiMsgs.unrolling', 'Unrolling ancient parchments'),
      t('share.aiMsgs.charting', 'Charting mystical territories'),
      t('share.aiMsgs.summoning', 'Summoning cartographic spirits'),
      t('share.aiMsgs.inkingCompass', 'Inking compass roses'),
      t('share.aiMsgs.aligning', 'Aligning celestial coordinates'),
      t('share.aiMsgs.aging', 'Aging the parchment'),
      t('share.aiMsgs.consultingStars', 'Consulting the stars'),
      t('share.aiMsgs.burnishing', 'Burnishing golden details'),
      t('share.aiMsgs.tracing', 'Tracing your epic journey'),
      t('share.aiMsgs.annotating', 'Annotating landmarks'),
      t('share.aiMsgs.surveying', 'Surveying mythical realms'),
      t('share.aiMsgs.binding', 'Binding the tome'),
      t('share.aiMsgs.polishing', 'Polishing the legend'),
      t('share.aiMsgs.mappingEdge', 'Mapping the edge of the world')
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  };
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentAiMessage, setCurrentAiMessage] = useState('');
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null);
  const [generationTimes, setGenerationTimes] = useState(null);
  const [threeDRendererTimingStats, set3DRendererRenderTimingStats] = useState(null);
  const [timingStatsLoaded, setTimingStatsLoaded] = useState(false);
  const [directStravaShareUiState, setDirectStravaShareUiState] = useState('idle');
  const renderer3DRenderCacheHit = requestData?.renderer3DRenderCacheHit ?? false;
  const renderer3DRenderCacheStatus = requestData?.renderer3DRenderCacheStatus || null;

  // Check if generation is in progress OR if generation is complete but generated image isn't ready yet
  const isGenerating = requestData && (
    (requestData.status !== 'openai_processing_complete' && !requestData.error) ||
    (requestData.status === 'openai_processing_complete' && (!post || !post.images || post.images.length === 0 || !post.images[0]))
  );
  const resolvedMedia = resolvePostMedia(post);
  // Render-only Zwift maps: show the Strava-style original first, then the
  // branded Zwift map — generated-first would put two similar renders side
  // by side with no story.
  const media = requestData?.renderOnly === true ?
    [...resolvedMedia].sort(
      (a, b) => (a.role === 'original' ? 0 : 1) - (b.role === 'original' ? 0 : 1)
    ) :
    resolvedMedia;
  const images = Array.isArray(post?.images) ? post.images.filter(Boolean) : [];
  const imageTypes = Array.isArray(post?.imageTypes) && post.imageTypes.length === images.length ?
    post.imageTypes :
    images.map((_, index) => index === 0 ? 'generated' : 'original');
  const currentMediaItem = media[currentImageIndex] || null;
  const {
    isCurrentVideoReady,
    progressFillRef,
    updateVideoProgress,
    videoRefs,
  } = useVideoProgressBar(
    currentImageIndex,
    currentMediaItem?.kind === 'video'
  );
  const currentImageType = currentMediaItem?.role || imageTypes[currentImageIndex] || imageTypes[0] || 'generated';
  const videoMediaItem = media.find((item) => item.kind === 'video') || null;
  const generatedMediaItem = media.find((item) => item.role === 'generated') || null;
  const generated2dMediaItem = media.find((item) => item.role === 'generated-2d') || null;
  const originalMediaItem = media.find((item) => item.role === 'original') || null;
  // Render-only maps have no AI transformation, so there is no transition
  // video to offer in the share flows.
  const stravaTransitionVideoItem = requestData?.renderOnly === true ? null : (videoMediaItem || (
    generatedMediaItem && originalMediaItem ? {
      kind: 'transition-preview',
      role: 'video',
      beforeUri: originalMediaItem.uri,
      afterUri: generatedMediaItem.uri,
    } : null
  ));

  // Track generation→complete transition for crossfade
  const [imageRevealed, setImageRevealed] = useState(!isGenerating && media.length > 0);
  const wasGeneratingRef = useRef(isGenerating);
  const directStravaShareResetTimerRef = useRef(null);
  const etaDebugLogSignatureRef = useRef(null);
  const rendererEtaCalibrationRef = useRef({
    status: null,
    delaySeconds: null,
    executionSeconds: null,
    observedAtMs: null,
  });
  const orientation = requestData?.mapOrientation || post?.mapOrientation || 'landscape';

  useEffect(() => {
    // Detect transition from generating → complete with media
    if (wasGeneratingRef.current && !isGenerating && media.length > 0) {
      // Keep imageRevealed false briefly so the fade-in animation plays
      setImageRevealed(false);
      const timer = setTimeout(() => setImageRevealed(true), 50);
      return () => clearTimeout(timer);
    }
    if (!isGenerating && media.length > 0) {
      setImageRevealed(true);
    }
    wasGeneratingRef.current = isGenerating;
  }, [isGenerating, media.length]);

  // Default carousel position. Render-only maps keep the Strava-style
  // original first in the strip but open on the branded map; everything else
  // opens on the first item. defaultImageIndex is a primitive, so this only
  // re-fires when the post or the computed default actually changes — user
  // swipes are never overridden once media has settled.
  const generatedMediaIndex = media.findIndex((item) => item.role === 'generated');
  const defaultImageIndex =
    requestData?.renderOnly === true && generatedMediaIndex >= 0 ? generatedMediaIndex : 0;

  useEffect(() => {
    setCurrentImageIndex(defaultImageIndex);
  }, [post?.id, defaultImageIndex]);

  useEffect(() => {
    if (directStravaShareResetTimerRef.current) {
      clearTimeout(directStravaShareResetTimerRef.current);
      directStravaShareResetTimerRef.current = null;
    }
    setDirectStravaShareUiState('idle');
  }, [post?.id]);

  useEffect(() => {
    return () => {
      if (directStravaShareResetTimerRef.current) {
        clearTimeout(directStravaShareResetTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (currentImageIndex >= media.length && media.length > 0) {
      setCurrentImageIndex(0);
    }
  }, [currentImageIndex, media.length]);

  useEffect(() => {
    const status = requestData?.status || null;
    const delaySeconds = toFiniteSeconds(requestData?.renderer3DDelayTime, 1000);
    const executionSeconds = toFiniteSeconds(requestData?.renderer3DExecutionTime, 1000);
    const previous = rendererEtaCalibrationRef.current;
    const timingChanged =
      previous.status !== status ||
      previous.delaySeconds !== delaySeconds ||
      previous.executionSeconds !== executionSeconds;

    if (status === 'renderer_3d_in_progress') {
      rendererEtaCalibrationRef.current = timingChanged ? {
        status,
        delaySeconds,
        executionSeconds,
        observedAtMs: Date.now(),
      } : previous;
      return;
    }

    rendererEtaCalibrationRef.current = {
      status,
      delaySeconds,
      executionSeconds,
      observedAtMs: timingChanged ? Date.now() : previous.observedAtMs,
    };
  }, [requestData?.status, requestData?.renderer3DDelayTime, requestData?.renderer3DExecutionTime]);

  const getImageLabel = (imageType) => {
    switch (imageType) {
      case 'original':
        return t('managePost.originalStravaMap', 'Original Strava map');
      case 'video':
        return t('managePost.transitionVideo', 'Transition video');
      default:
        return t('managePost.generatedMap', 'Mythic map');
    }
  };

  // Fetch generation times from Firestore
  useEffect(() => {
    let cancelled = false;

    const fetchTimingStats = async () => {
      try {
        const [generationStatsDoc, rendererStatsDoc] = await Promise.all([
          getDoc(doc(db, 'systemStats', 'generationTimesByModel')),
          getDoc(doc(db, 'systemStats', 'threeDRendererRenderTimes'))
        ]);

        if (!cancelled && generationStatsDoc.exists()) {
          setGenerationTimes(generationStatsDoc.data());
        }

        if (!cancelled && rendererStatsDoc.exists()) {
          set3DRendererRenderTimingStats(rendererStatsDoc.data());
        }
      } catch (error) {
        console.error('Error fetching timing stats:', error);
      } finally {
        if (!cancelled) {
          setTimingStatsLoaded(true);
        }
      }
    };

    fetchTimingStats();
    return () => {
      cancelled = true;
    };
  }, []);
  
  useEffect(() => {
    if (!isGenerating || !requestData?.generationStartTime || !timingStatsLoaded) {
      etaDebugLogSignatureRef.current = null;
      return;
    }

    const breakdown = calculateEtaBreakdown({
      requestData,
      generationTimes,
      threeDRendererTimingStats,
      renderer3DRenderCacheHit,
      renderer3DRenderCacheStatus,
      rendererEtaCalibration: rendererEtaCalibrationRef.current,
    });

    if (!breakdown) {
      return;
    }

    const logSignature = JSON.stringify({
      status: breakdown.status,
      model: breakdown.model,
      isRendererFlow: breakdown.isRendererFlow,
      cacheReady: breakdown.cacheReady,
      renderer3DRenderCacheHit: breakdown.renderer3DRenderCacheHit,
      renderer3DRenderCacheStatus: breakdown.renderer3DRenderCacheStatus,
      modelAverageSeconds: Math.round(breakdown.modelAverageSeconds),
      modelRemainingSeconds: Math.round(breakdown.modelRemainingSeconds),
      rendererAverageSeconds: Math.round(breakdown.rendererAverageSeconds),
      rendererRemainingSeconds: Math.round(breakdown.rendererRemainingSeconds),
      rendererDelaySeconds: breakdown.rendererDelaySeconds !== null ? Math.round(breakdown.rendererDelaySeconds) : null,
      rendererExecutionSeconds: breakdown.rendererExecutionSeconds !== null ? Math.round(breakdown.rendererExecutionSeconds) : null,
      calibratedDelaySeconds: breakdown.calibratedDelaySeconds !== null ? Math.round(breakdown.calibratedDelaySeconds) : null,
      calibratedExecutionSeconds: breakdown.calibratedExecutionSeconds !== null ? Math.round(breakdown.calibratedExecutionSeconds) : null,
      rendererRecordedDurationSeconds: breakdown.rendererRecordedDurationSeconds !== null ? Math.round(breakdown.rendererRecordedDurationSeconds) : null,
      remainingSeconds: breakdown.remainingSeconds,
      modelEtaSource: breakdown.modelEtaSource,
      rendererEtaSource: breakdown.rendererEtaSource,
    });

    if (etaDebugLogSignatureRef.current === logSignature) {
      return;
    }

    etaDebugLogSignatureRef.current = logSignature;
    console.log('[ManagePostCard ETA]', {
      postId: post?.id || null,
      requestStatus: breakdown.status,
      elapsedSeconds: Math.round(breakdown.elapsedSeconds),
      totalRemainingSeconds: breakdown.remainingSeconds,
      model: {
        averageSeconds: Math.round(breakdown.modelAverageSeconds),
        remainingSeconds: Math.round(breakdown.modelRemainingSeconds),
        source: breakdown.modelEtaSource,
      },
      renderer3D: {
        enabled: breakdown.isRendererFlow,
        cacheReady: breakdown.cacheReady,
        averageSeconds: Math.round(breakdown.rendererAverageSeconds),
        remainingSeconds: Math.round(breakdown.rendererRemainingSeconds),
        source: breakdown.rendererEtaSource,
        delaySeconds: breakdown.rendererDelaySeconds !== null ? Math.round(breakdown.rendererDelaySeconds) : null,
        executionSeconds: breakdown.rendererExecutionSeconds !== null ? Math.round(breakdown.rendererExecutionSeconds) : null,
        calibratedDelaySeconds: breakdown.calibratedDelaySeconds !== null ? Math.round(breakdown.calibratedDelaySeconds) : null,
        calibratedExecutionSeconds: breakdown.calibratedExecutionSeconds !== null ? Math.round(breakdown.calibratedExecutionSeconds) : null,
        recordedDurationSeconds: breakdown.rendererRecordedDurationSeconds !== null ? Math.round(breakdown.rendererRecordedDurationSeconds) : null,
        cacheHit: breakdown.renderer3DRenderCacheHit,
        cacheStatus: breakdown.renderer3DRenderCacheStatus,
      },
      totals: {
        baseExpectedSeconds: breakdown.baseExpectedSeconds,
        dynamicExpectedSeconds: breakdown.dynamicExpectedSeconds,
      },
    });
  }, [
    isGenerating,
    post?.id,
    requestData?.generationStartTime,
    requestData?.status,
    requestData?.use3DMap,
    requestData?.mapSource,
    requestData?.generationModel,
    requestData?.threeDRenderSource,
    requestData?.renderer3DDelayTime,
    requestData?.renderer3DExecutionTime,
    requestData?.renderer3DRenderDurationSeconds,
    renderer3DRenderCacheHit,
    renderer3DRenderCacheStatus,
    timingStatsLoaded,
    generationTimes,
    threeDRendererTimingStats,
  ]);

  // Initialize ETA after timing stats are fetched so we do not show placeholder defaults.
  useEffect(() => {
    if (!isGenerating || !requestData?.generationStartTime || !timingStatsLoaded) {
      setEstimatedTimeRemaining(null);
      return;
    }

    const breakdown = calculateEtaBreakdown({
      requestData,
      generationTimes,
      threeDRendererTimingStats,
      renderer3DRenderCacheHit,
      renderer3DRenderCacheStatus,
      rendererEtaCalibration: rendererEtaCalibrationRef.current,
    });

    setEstimatedTimeRemaining(breakdown ? breakdown.remainingSeconds : null);
  }, [
    isGenerating,
    requestData?.generationStartTime,
    requestData?.status,
    requestData?.use3DMap,
    requestData?.mapSource,
    requestData?.generationModel,
    requestData?.threeDRenderSource,
    requestData?.renderer3DDelayTime,
    requestData?.renderer3DExecutionTime,
    requestData?.renderer3DRenderDurationSeconds,
    renderer3DRenderCacheHit,
    renderer3DRenderCacheStatus,
    timingStatsLoaded,
    generationTimes,
    threeDRendererTimingStats,
  ]);

  // Rotate AI messages during generation
  useEffect(() => {
    if (isGenerating && requestData.status === 'openai_processing') {
      // Set initial random message
      setCurrentAiMessage(getAIMessage());
      
      // Rotate messages every 2-4 seconds
      const interval = setInterval(() => {
        setCurrentAiMessage(getAIMessage());
      }, 2000 + Math.random() * 2000);
      
      return () => clearInterval(interval);
    }
  }, [isGenerating, requestData?.status]);

  // Calculate and update progress in real-time
  useEffect(() => {
    if (!isGenerating || !requestData?.generationStartTime) return;

    const updateProgress = () => {
      const breakdown = calculateEtaBreakdown({
        requestData,
        generationTimes,
        threeDRendererTimingStats,
        renderer3DRenderCacheHit,
        renderer3DRenderCacheStatus,
        rendererEtaCalibration: rendererEtaCalibrationRef.current,
        nowMs: Date.now(),
      });

      if (!breakdown) {
        return;
      }

      const elapsed = breakdown.elapsedSeconds;

      // Calculate progress - allow up to 100% but don't exceed it
      const timeProgress = Math.min((elapsed / breakdown.dynamicExpectedSeconds) * 100, 100);
      
      // Status-based progress
      let statusProgress = 0;
      switch (requestData.status) {
        case 'fetching_activity_details':
        case 'initiating_gpx_download':
          statusProgress = 2;
          break;
        case 'gpx_downloading_authenticated':
        case 'gpx_downloading_unauthenticated':
          statusProgress = 4;
          break;
        case 'geodata_created':
          statusProgress = 6;
          break;
        case 'mapbox_generating':
          statusProgress = 8;
          break;
        case 'mapbox_generated':
          statusProgress = 10;
          break;
        case 'renderer_3d_starting':
          statusProgress = 11;
          break;
        case 'renderer_3d_queued':
          statusProgress = 13;
          break;
        case 'renderer_3d_in_progress':
          statusProgress = 16;
          break;
        case 'renderer_3d_uploading':
          statusProgress = 19;
          break;
        case 'renderer_3d_cached':
          statusProgress = 20;
          break;
        case 'openai_processing':
          statusProgress = 25;
          break;
        default:
          statusProgress = 0;
      }
      
      setProgressPercentage(Math.max(timeProgress, statusProgress));
      
      if (timingStatsLoaded) {
        if (breakdown.remainingSeconds > 0) {
          setEstimatedTimeRemaining(breakdown.remainingSeconds);
        } else {
          // When time is up, show "Almost done" instead of hiding
          setEstimatedTimeRemaining(0);
        }
      } else {
        setEstimatedTimeRemaining(null);
      }
    };
    
    updateProgress();
    const interval = setInterval(updateProgress, 1000);
    return () => clearInterval(interval);
  }, [
    isGenerating,
    requestData?.generationStartTime,
    requestData?.status,
    requestData?.use3DMap,
    requestData?.mapSource,
    requestData?.generationModel,
    requestData?.threeDRenderSource,
    requestData?.renderer3DDelayTime,
    requestData?.renderer3DExecutionTime,
    requestData?.renderer3DRenderDurationSeconds,
    renderer3DRenderCacheHit,
    renderer3DRenderCacheStatus,
    timingStatsLoaded,
    generationTimes,
    threeDRendererTimingStats,
  ]);

  // Modal states
  const [showEditStatsModal, setShowEditStatsModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [editedStatsText, setEditedStatsText] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [isTryingAgain, setIsTryingAgain] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [publishEarnedDiamond, setPublishEarnedDiamond] = useState(false);
  const [stravaShareInitiated, setStravaShareInitiated] = useState(false);
  const [isCheckingWatermark, setIsCheckingWatermark] = useState(false);
  const [hasClaimedWatermarkCredit, setHasClaimedWatermarkCredit] = useState(false);
  const [optimisticStravaCreditClaimed, setOptimisticStravaCreditClaimed] = useState(false);
  const [showWatermarkSuccessModal, setShowWatermarkSuccessModal] = useState(false);
  const [showStravaShareErrorModal, setShowStravaShareErrorModal] = useState(false);
  const [stravaShareErrorMessage, setStravaShareErrorMessage] = useState('');
  const [stravaShareSuccessMode, setStravaShareSuccessMode] = useState('manual');
  const [stravaShareFlowMode, setStravaShareFlowMode] = useState(DEFAULT_STRAVA_SHARE_FLOW_MODE);
  const [shareSelection, setShareSelection] = useState({ generated: true, video: true, original: true });
  const [showStravaShareModal, setShowStravaShareModal] = useState(false);
  const [stravaShareSelection, setStravaShareSelection] = useState({ ...DEFAULT_MEDIA_SELECTION });
  const [autoUploadEnabled, setAutoUploadEnabled] = useState(false);
  const [autoUploadHasProfileScope, setAutoUploadHasProfileScope] = useState(true);
  const [hasLoadedAutoUploadPreference, setHasLoadedAutoUploadPreference] = useState(false);
  const [shouldShowAutoUploadToggleForModal, setShouldShowAutoUploadToggleForModal] = useState(false);
  const [isAutoUploadPreferenceLoading, setIsAutoUploadPreferenceLoading] = useState(false);
  const [isAutoUploadPreferenceSaving, setIsAutoUploadPreferenceSaving] = useState(false);

  const isCompletedAutoUpload =
    requestData?.source === 'auto_upload' &&
    requestData?.skipCreditCheck === true &&
    requestData?.status === 'openai_processing_complete' &&
    !requestData?.error;
  const shouldSkipWatermarkCreditCheck =
    requestData?.skipCreditCheck === true &&
    requestData?.isRegeneration !== true;

  useEffect(() => {
    setStravaShareInitiated(false);
    setHasClaimedWatermarkCredit(false);
    setOptimisticStravaCreditClaimed(false);
    setStravaShareSuccessMode('manual');
  }, [post.id]);

  // Reset share selection when either modal opens. Items are greyed out and
  // unticked when the post doesn't have them yet, so the user can't share
  // something that doesn't exist. Depend on booleans (not the media item
  // objects) so the effect doesn't refire on every render when media.find()
  // returns a fresh ref.
  const hasGeneratedMedia = Boolean(generatedMediaItem);
  const hasGenerated2dMedia = Boolean(generated2dMediaItem);
  const hasVideoMedia = Boolean(videoMediaItem);
  const hasStravaTransitionVideo = Boolean(stravaTransitionVideoItem);
  const hasOriginalMedia = Boolean(originalMediaItem);
  // Render-only Zwift posts share only the Zwift map(s) to Strava — no video
  // exists and the Strava-style original isn't offered.
  const isRenderOnlyPost = requestData?.renderOnly === true;
  useEffect(() => {
    if (showShareModal) {
      setShareSelection({
        generated: hasGeneratedMedia,
        video: hasVideoMedia,
        original: hasOriginalMedia,
      });
    }
  }, [showShareModal, hasGeneratedMedia, hasVideoMedia, hasOriginalMedia]);
  useEffect(() => {
    if (showStravaShareModal) {
      const cachedSelection = readCachedStravaShareSelection(user?.uid) || DEFAULT_MEDIA_SELECTION;
      setStravaShareSelection(applyMediaAvailability(cachedSelection, {
        generated: hasGeneratedMedia,
        generated2d: hasGenerated2dMedia,
        video: hasStravaTransitionVideo,
        original: hasOriginalMedia && !isRenderOnlyPost,
      }));
    }
  }, [showStravaShareModal, hasGeneratedMedia, hasGenerated2dMedia, hasStravaTransitionVideo, hasOriginalMedia, isRenderOnlyPost, user?.uid]);

  useEffect(() => {
    let isCancelled = false;

    if (!showStravaShareModal || !user?.uid) {
      setHasLoadedAutoUploadPreference(false);
      setShouldShowAutoUploadToggleForModal(false);
      return () => {
        isCancelled = true;
      };
    }

    const loadAutoUploadPreference = async () => {
      setHasLoadedAutoUploadPreference(false);
      setIsAutoUploadPreferenceLoading(true);
      try {
        const [userSnap, tokenSnap] = await Promise.all([
          getDoc(doc(db, 'users', user.uid)),
          getDoc(doc(db, 'users', user.uid, 'stravaTokens', 'tokens')),
        ]);

        if (isCancelled) {
          return;
        }

        const userData = userSnap.data() || {};
        const tokenData = tokenSnap.data() || {};
        const autoUploadSettings = userData.settings?.autoUpload || {};
        const scopeStr = typeof tokenData.scope === 'string' ? tokenData.scope : '';
        // Art-map share surface: track the art toggle (with the legacy fallback).
        const loadedAutoUploadEnabled = typeof autoUploadSettings.artEnabled === 'boolean'
          ? autoUploadSettings.artEnabled
          : (autoUploadSettings.enabled === true && autoUploadSettings.zwiftOnly !== true);

        setAutoUploadEnabled(loadedAutoUploadEnabled);
        setShouldShowAutoUploadToggleForModal(!loadedAutoUploadEnabled);
        setAutoUploadHasProfileScope(scopeStr.includes('profile:read_all'));
        setHasLoadedAutoUploadPreference(true);
      } catch (error) {
        if (!isCancelled) {
          console.warn('ManagePostCard: failed to load auto-upload preference', error);
          setAutoUploadHasProfileScope(true);
        }
      } finally {
        if (!isCancelled) {
          setIsAutoUploadPreferenceLoading(false);
        }
      }
    };

    loadAutoUploadPreference();

    return () => {
      isCancelled = true;
    };
  }, [showStravaShareModal, user?.uid]);

  // Check if post is already published
  useEffect(() => {
    if (requestData?.isPublic) {
      setIsPublished(true);
    }
  }, [requestData?.isPublic]);

  useEffect(() => {
    if (!post.stravaActivityId || !isCompletedAutoUpload) {
      return;
    }

    setStravaShareInitiated(true);
    setStravaShareSuccessMode('direct');
  }, [post.stravaActivityId, isCompletedAutoUpload]);

  // Check if watermark credit was already claimed
  useEffect(() => {
    const checkWatermarkStatus = async () => {
      if (!user || !post.id || !post.stravaActivityId) return;
      if (shouldSkipWatermarkCreditCheck) return;

      try {
        // Check if this activity already has watermark credit
        const checkCreditFunc = httpsCallable(functions, 'checkActivityWatermarkCredit');
        const result = await checkCreditFunc({ activityId: post.stravaActivityId });

        if (result.data.claimed) {
          setHasClaimedWatermarkCredit(true);
          setStravaShareInitiated(true); // Show the claimed state
        }
      } catch (error) {
        console.error('Error checking watermark credit status:', error);
      }
    };

    checkWatermarkStatus();
  }, [user, post.id, post.stravaActivityId, shouldSkipWatermarkCreditCheck]);

  useEffect(() => {
    let isCancelled = false;

    const loadStravaShareFlowMode = async () => {
      if (!user) {
        return;
      }

      try {
        const settingsSnap = await getDoc(doc(db, 'settings', 'mapGeneration'));
        if (!isCancelled) {
          setStravaShareFlowMode(normalizeStravaShareFlowMode(settingsSnap.data()?.stravaShareFlowMode));
        }
      } catch (error) {
        console.error('Error loading Strava share flow mode:', error);
        if (!isCancelled) {
          setStravaShareFlowMode(DEFAULT_STRAVA_SHARE_FLOW_MODE);
        }
      }
    };

    loadStravaShareFlowMode();

    return () => {
      isCancelled = true;
    };
  }, [user]);

  // Auto-check for watermark when returning from Strava share
  useEffect(() => {
    const autoCheckedRef = { current: false };

    const checkPendingWatermarkShare = async () => {
      // Early exit if already auto-checked or check in progress
      if (autoCheckedRef.current || isCheckingWatermark || hasClaimedWatermarkCredit) {
        return;
      }

      if (!user || !post.id || !post.stravaActivityId) {
        return;
      }

      try {
        const pendingDataStr = localStorage.getItem('watermark_share_pending');
        if (!pendingDataStr) return;

        const pendingData = JSON.parse(pendingDataStr);

        // Check if this is the same user, activity, and request
        if (pendingData.userId === user.uid &&
            pendingData.activityId === post.stravaActivityId &&
            pendingData.requestId === post.id) {

          // Check if it's been less than 1 hour since they clicked share
          const timeDiff = Date.now() - pendingData.timestamp;
          if (timeDiff < 60 * 60 * 1000) { // 1 hour
            // Mark that we're attempting auto-check
            autoCheckedRef.current = true;

            // Reflect the successful share state in the main Strava button.
            setStravaShareInitiated(true);
            setStravaShareSuccessMode(pendingData.shareMode === 'direct' ? 'direct' : 'manual');

            if (pendingData.shareMode === 'direct') {
              localStorage.removeItem('watermark_share_pending');
              return;
            }

            // Run the check automatically (inline to control localStorage clearing)
            console.log('Auto-checking for watermark after Strava share...');

            setIsCheckingWatermark(true);
            try {
              const checkWatermarksFunc = httpsCallable(functions, 'checkActivityWatermarks');
              const result = await checkWatermarksFunc({ requestId: post.id });

              if (result.data.summary?.hasWatermark) {
                setHasClaimedWatermarkCredit(true);
                setShowWatermarkSuccessModal(true);

                // Track PostHog event for auto-detected credit claim
                posthog?.capture('claimed_strava_share_credit', {
                  activity_id: post.stravaActivityId,
                  post_id: post.id,
                  credits_awarded: result.data.summary?.creditsAwarded || 1,
                  auto_detected: true,
                  page: 'manage_post'
                });

                if (onCreditsAwarded) {
                  onCreditsAwarded();
                }
                // Clear pending data on success
                localStorage.removeItem('watermark_share_pending');
              } else {
                // Map not detected - clear pending data to prevent re-checking
                localStorage.removeItem('watermark_share_pending');
                // Don't show error modal on auto-check, user can manually check if they want
                console.log('Auto-check: Map not detected yet');
              }
            } catch (error) {
              console.error('Auto-check error:', error);
              // Clear pending data on error to prevent retry loop
              localStorage.removeItem('watermark_share_pending');
            } finally {
              setIsCheckingWatermark(false);
            }
          } else {
            // Remove stale pending data
            localStorage.removeItem('watermark_share_pending');
          }
        }
      } catch (error) {
        console.error('Error checking pending watermark share:', error);
      }
    };

    // Check on mount
    checkPendingWatermarkShare();

    let debounceTimer = null;

    // Check when tab gains focus or becomes visible (with debouncing)
    const handleFocusOrVisibility = () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      debounceTimer = setTimeout(() => {
        checkPendingWatermarkShare();
      }, 100);
    };

    const handleFocus = () => {
      handleFocusOrVisibility();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleFocusOrVisibility();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [user, post.id, post.stravaActivityId, isCheckingWatermark, hasClaimedWatermarkCredit]);

  const handleEditStatsPanel = async () => {
    try {
      const updateStatsPanel = httpsCallable(functions, 'updateStatsPanel');
      await updateStatsPanel({ 
        requestId: post.id,
        newStatsText: editedStatsText 
      });
      
      // Notify parent to refresh credits (costs 1 diamond)
      if (onCreditsAwarded) {
        onCreditsAwarded();
      }
      
      setShowEditStatsModal(false);
      // Refresh the page or update local state
      window.location.reload();
    } catch (error) {
      console.error('Error updating stats panel:', error);
      alert(error.message || 'Failed to update stats panel');
    }
  };

  const handleTryAgain = async () => {
    if (isTryingAgain) return;

    // Track try again click
    posthog?.capture('try_again_clicked', {
      request_id: post.id,
      strava_activity_id: post.stravaActivityId,
      page: 'manage_post'
    });

    setIsTryingAgain(true);
    try {
      const regenerateMapFunc = httpsCallable(functions, 'regenerateMap');
      const result = await regenerateMapFunc({ originalRequestId: post.id });
      
      if (result.data.success) {
        navigate(`/manage-post/${result.data.newRequestId}`);
      }
    } catch (error) {
      console.error('Error regenerating map:', error);
      alert(error.message || 'Failed to regenerate map');
    } finally {
      setIsTryingAgain(false);
    }
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    setPublishSuccess(false);
    setPublishEarnedDiamond(false);
    
    try {
      const toggleMapVisibilityFunc = httpsCallable(functions, 'toggleMapVisibility');
      const result = await toggleMapVisibilityFunc({ 
        requestId: post.id,
        isPublic: true 
      });
      
      if (result.data.success) {
        setIsPublished(true);
        setPublishSuccess(true);

        // Track map publication to feed
        posthog?.capture('map_published_to_feed', {
          request_id: post.id,
          activity_name: post.caption || requestData?.name || 'Unknown',
          activity_type: post.activityType,
          strava_activity_id: post.stravaActivityId,
          earned_diamond: result.data.creditAwarded || false,
          page: 'manage_post'
        });

        // Check if credit was awarded
        if (result.data.creditAwarded) {
          setPublishEarnedDiamond(true);
          if (onCreditsAwarded) {
            onCreditsAwarded();
          }
        }

        // Keep modal open to show success message
      }
    } catch (error) {
      console.error('Error publishing to feed:', error);
      alert(error.message || 'Failed to publish');
      setShowPublishModal(false);
    } finally {
      setIsPublishing(false);
    }
  };


  // Download a single media item by fetching the bytes and triggering an anchor click.
  const downloadMediaItem = async (item, index) => {
    const response = await fetch(item.uri);
    const blob = await response.blob();
    const extension = item.kind === 'video' ? 'mp4' : 'png';
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `mythic_map_${post.id}_${item.role}${index ? `_${index}` : ''}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  // Map the share-modal checkbox state onto the actual media items the post has.
  const getSelectedMediaItems = () => {
    const items = [];
    if (shareSelection.generated && generatedMediaItem) items.push(generatedMediaItem);
    if (shareSelection.video && videoMediaItem) items.push(videoMediaItem);
    if (shareSelection.original && originalMediaItem) items.push(originalMediaItem);
    return items;
  };

  const handleDownload = async () => {
    if (isDownloading) return;

    const items = getSelectedMediaItems();
    if (items.length === 0) return;

    setIsDownloading(true);
    try {
      // Browsers will prompt the user once when multiple downloads fire from one
      // user action; subsequent files come through automatically. A short delay
      // between downloads gives the browser time to register each one.
      for (let i = 0; i < items.length; i += 1) {
        await downloadMediaItem(items[i], i);
        if (i < items.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 400));
        }
      }

      posthog?.capture('map_downloaded', {
        request_id: post.id,
        download_type: items.map((item) => item.role).join(','),
        download_count: items.length,
        page: 'manage_post'
      });
    } catch (error) {
      console.error('Error downloading media:', error);
      alert('Failed to download media');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopyLink = () => {
    const shareUrl = `${window.location.origin}/post/${post.id}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      alert(t('managePost.linkCopied', 'Link copied to clipboard!'));
    });
  };

  const handleShare = (platform) => {
    const shareUrl = `${window.location.origin}/post/${post.id}`;
    const shareText = `Check out my epic adventure on Mythic Maps!`;

    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'instagram':
      case 'tiktok':
        // Instagram and TikTok have no web share endpoint, so the established
        // workflow is to download the media and let the user upload it manually.
        handleDownload();
        break;
      case 'reddit':
        window.open(`https://reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`, '_blank');
        break;
    }
  };

  const selectedShareCount = (shareSelection.generated && generatedMediaItem ? 1 : 0) +
    (shareSelection.video && videoMediaItem ? 1 : 0) +
    (shareSelection.original && originalMediaItem ? 1 : 0);
  const hasShareSelection = selectedShareCount > 0;
  const toggleShareSelection = (key) => {
    setShareSelection((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const toggleStravaShareSelection = (key) => {
    setStravaShareSelection((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      const cachedSelection = readCachedStravaShareSelection(user?.uid) || DEFAULT_MEDIA_SELECTION;
      writeCachedStravaShareSelection(user?.uid, {
        ...cachedSelection,
        [key]: next[key],
      });
      return next;
    });
  };

  const handleAutoUploadToggle = async (event) => {
    const nextEnabled = event.target.checked;
    if (!user?.uid || isAutoUploadPreferenceSaving) {
      return;
    }

    if (nextEnabled && !autoUploadHasProfileScope) {
      navigate('/settings#auto-upload');
      return;
    }

    const previousEnabled = autoUploadEnabled;
    setAutoUploadEnabled(nextEnabled);
    onAutoUploadEnabledChange?.(nextEnabled);
    setIsAutoUploadPreferenceSaving(true);

    try {
      const updatePayload = {
        'settings.autoUpload.artEnabled': nextEnabled,
        'settings.autoUploadDiscoverySeen': true,
      };

      if (nextEnabled) {
        const futureUploadSelection = normalizeMediaSelection(
          readCachedStravaShareSelection(user.uid) || stravaShareSelection
        );
        const hasFutureUploadSelection = futureUploadSelection.generated ||
          futureUploadSelection.video ||
          futureUploadSelection.original;

        if (hasFutureUploadSelection) {
          updatePayload['settings.autoUpload.includeGeneratedMap'] = futureUploadSelection.generated;
          updatePayload['settings.autoUpload.includeTransitionVideo'] = futureUploadSelection.video;
          updatePayload['settings.autoUpload.includeOriginalMap'] = futureUploadSelection.original;
        }
      }

      await updateDoc(doc(db, 'users', user.uid), updatePayload);

      posthog?.capture('auto_upload_toggled_from_strava_share_modal', {
        enabled: nextEnabled,
        request_id: post.id,
        activity_id: post.stravaActivityId,
        page: 'manage_post'
      });
    } catch (error) {
      console.warn('ManagePostCard: failed to update auto-upload preference', error);
      setAutoUploadEnabled(previousEnabled);
      onAutoUploadEnabledChange?.(previousEnabled);
      alert(t('managePost.autoUploadUpdateError', 'Could not update auto-upload. Please try from settings.'));
    } finally {
      setIsAutoUploadPreferenceSaving(false);
    }
  };

  const savePendingStravaShare = (shareMode = 'manual') => {
    if (!user || !post.stravaActivityId) return;

    const sessionId = Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    const pendingData = {
      userId: user.uid,
      activityId: post.stravaActivityId,
      requestId: post.id,
      timestamp: Date.now(),
      sessionId,
      shareMode: shareMode === 'direct' ? 'direct' : 'manual'
    };

    localStorage.setItem('watermark_share_pending', JSON.stringify(pendingData));
  };

  const getCallableErrorMessage = (error, fallbackMessage) => {
    if (typeof error?.details === 'string' && error.details.trim()) {
      return error.details;
    }

    if (typeof error?.message === 'string' && error.message.trim()) {
      const cleanedMessage = error.message.replace(/^.*?:\s*/, '').trim();
      return cleanedMessage || fallbackMessage;
    }

    return fallbackMessage;
  };

  const scheduleDirectStravaShareButtonReset = () => {
    if (directStravaShareResetTimerRef.current) {
      clearTimeout(directStravaShareResetTimerRef.current);
    }

    directStravaShareResetTimerRef.current = setTimeout(() => {
      setDirectStravaShareUiState('idle');
      setStravaShareInitiated(false);
      directStravaShareResetTimerRef.current = null;
    }, 3000);
  };

  const shouldUseDirectStravaShare =
    stravaShareFlowMode === 'all_media_upload' ||
    (stravaShareFlowMode === 'admin_media_upload' && actualIsAdmin);

  // Direct upload code path. Called from the new Strava share modal CTA, with
  // a selection object describing which of {generated, video, original} to send.
  const runDirectStravaUpload = async (selection) => {
    if (!post.stravaActivityId) return;

    if (directStravaShareResetTimerRef.current) {
      clearTimeout(directStravaShareResetTimerRef.current);
      directStravaShareResetTimerRef.current = null;
    }
    setDirectStravaShareUiState('sharing');
    setIsDownloading(true);
    try {
      const uploadMapToStravaMedia = httpsCallable(functions, 'uploadMapToStravaMedia');
      const result = await uploadMapToStravaMedia({
        requestId: post.id,
        includeGenerated: selection.generated && Boolean(generatedMediaItem),
        includeGenerated2d: Boolean(selection.generated2d) && Boolean(generated2dMediaItem),
        includeVideo: selection.video && Boolean(stravaTransitionVideoItem),
        includeOriginal: selection.original && Boolean(originalMediaItem) && !isRenderOnlyPost,
      });
      const isQueuedDirectUpload = result.data?.queued === true;

      localStorage.removeItem('watermark_share_pending');
      setStravaShareSuccessMode('direct');
      setStravaShareInitiated(true);
      setDirectStravaShareUiState('success');
      if (isQueuedDirectUpload) {
        setOptimisticStravaCreditClaimed(true);
      }
      scheduleDirectStravaShareButtonReset();

      if (result.data?.creditAwarded || result.data?.creditAlreadyClaimed) {
        setHasClaimedWatermarkCredit(true);
        setOptimisticStravaCreditClaimed(false);
      }

      if (result.data?.creditAwarded) {
        setHasClaimedWatermarkCredit(true);
        setOptimisticStravaCreditClaimed(false);
        setShowWatermarkSuccessModal(true);

        posthog?.capture('claimed_strava_share_credit', {
          activity_id: post.stravaActivityId,
          post_id: post.id,
          credits_awarded: 1,
          auto_detected: true,
          page: 'manage_post'
        });

        if (onCreditsAwarded) {
          onCreditsAwarded();
        }
      }
    } catch (error) {
      setDirectStravaShareUiState('idle');
      console.error('Error uploading map directly to Strava:', error);
      setStravaShareErrorMessage(
        getCallableErrorMessage(
          error,
          t('managePost.directStravaUploadError', 'Failed to upload the map directly to Strava.')
        )
      );
      setShowStravaShareErrorModal(true);
    } finally {
      setIsDownloading(false);
    }
  };

  // Top-level click handler for the Strava share button. In direct mode it
  // opens a modal so the user can pick which media items to upload; in manual
  // mode it kicks off the legacy download + Strava-edit redirect flow.
  const handleStravaShare = async () => {
    if (!post.stravaActivityId) return;
    if (shouldUseDirectStravaShare) {
      setShowStravaShareModal(true);
      return;
    }

    const shouldTrackWatermarkCredit = !hasClaimedWatermarkCredit;

    // Check if device is iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    // Save pending state to localStorage for watermark auto-check
    // This is critical for the backend to know the user shared to Strava
    if (shouldTrackWatermarkCredit) {
      savePendingStravaShare('manual');
    }

    if (isIOS) {
      // For iOS: Redirect to special iOS download page
      const imageUrlForIOS = post.images[0];
      const iosUrl = `/ios-download/${post.id}?imageUrl=${encodeURIComponent(imageUrlForIOS)}&stravaId=${post.stravaActivityId}`;
      window.location.href = iosUrl;
      return;
    }

    // For non-iOS devices: Continue with normal download
    setIsDownloading(true);
    try {
      const response = await fetch(post.images[0]);
      const blob = await response.blob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mythic_map_${post.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Then open the Strava edit page and reflect the successful share state in the button.
      setTimeout(() => {
        window.open(`https://www.strava.com/activities/${post.stravaActivityId}/edit`, '_blank');
        setStravaShareSuccessMode('manual');
        setStravaShareInitiated(true);
      }, 1000);
    } catch (error) {
      console.error('Error sharing to Strava:', error);
      alert('Failed to prepare Strava share');
    } finally {
      setIsDownloading(false);
    }
  };

  const formatDuration = (totalSeconds) => {
    if (!totalSeconds || isNaN(totalSeconds)) return 'N/A';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

	  const getStatusMessage = () => {
	    if (!requestData) return t('share.processing', 'Processing your mythic map');
    
    switch (requestData.status) {
      case 'initiating_gpx_download':
      case 'fetching_activity_details':
        return t('share.fetching', 'Fetching your activity data from Strava');
      case 'gpx_downloading_authenticated':
      case 'gpx_downloading_unauthenticated':
        return t('share.downloadingRoute', 'Downloading your activity route');
      case 'geodata_created':
        return t('share.processingRoute', 'Processing your route data');
      case 'mapbox_generating':
        return t('share.creatingVisualization', 'Creating your map visualization');
      case 'mapbox_generated':
        return t('share.generatingTransformation', 'Generating your mythic transformation');
      case 'renderer_3d_starting':
        return t('share.threeDRendererStarting', 'Waking the 3D cartographer');
      case 'renderer_3d_queued':
        return t('share.threeDRendererQueued', "Your route is waiting for the 3D cartographer's desk");
      case 'renderer_3d_in_progress':
        return t('share.threeDRendererRunning', 'The 3D cartographer is sketching your route in relief');
      case 'renderer_3d_uploading':
        return t('share.threeDRendererUploading', 'Sealing and saving the 3D render');
      case 'renderer_3d_cached':
        return t('share.threeDRendererCached', 'Reusing a cached 3D render');
      case 'openai_processing':
        return currentAiMessage || t('share.aiWorking', 'AI is working its magic on your map');
      default:
        return t('share.processing', 'Processing your mythic map');
    }
  };

  const etaText = estimatedTimeRemaining === null
    ? null
    : estimatedTimeRemaining > 0
      ? `${t('share.eta', 'ETA')}: ${formatDuration(estimatedTimeRemaining)}`
      : t('share.almostDone', 'Almost done');

  const getStravaShareButtonLabel = () => {
    if (directStravaShareUiState === 'sharing') {
      return t('managePost.sharingToStrava', 'Sharing to Strava...');
    }

    if (directStravaShareUiState === 'success') {
      return t('managePost.sharedToStravaSuccess', 'Shared to Strava');
    }

    if (isDownloading) {
      return t('managePost.preparing', 'Preparing...');
    }

    if (isCheckingWatermark) {
      return t('managePost.checking', 'Checking...');
    }

    if (hasClaimedWatermarkCredit || optimisticStravaCreditClaimed) {
      return t('managePost.shareToStravaCreditClaimed', 'Share to Strava • Credit claimed');
    }

    if (stravaShareInitiated) {
      return stravaShareSuccessMode === 'direct'
        ? t('managePost.uploadedToStrava', 'Uploaded to Strava')
        : t('managePost.sharedToStrava', 'Image Downloaded');
    }

    // Free generations (render-only Zwift maps, auto-uploads) earn no share
    // credit, so don't promise one.
    return shouldSkipWatermarkCreditCheck
      ? t('managePost.shareToStravaFree', 'Share to Strava')
      : t('managePost.shareToStrava', 'Share to Strava & Earn 💎');
  };

  const renderStravaShareButtonIcon = () => {
    if (directStravaShareUiState === 'sharing' || isDownloading || isCheckingWatermark) {
      return <div className="spinner-small"></div>;
    }

    if (
      directStravaShareUiState === 'success' ||
      optimisticStravaCreditClaimed ||
      (!hasClaimedWatermarkCredit && stravaShareInitiated)
    ) {
      return <IconCheck size={18} />;
    }

    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
      </svg>
    );
  };

  return (
    <article className="manage-post-card">
      {/* Image/Media Carousel OR Generation Progress — layered for crossfade */}
      <div className={`post-media media-crossfade ${orientation === 'portrait' ? 'portrait' : 'landscape'}`}>
        {/* Progress overlay layer */}
        {isGenerating && (
          <div className={`generation-progress-overlay ${orientation === 'portrait' ? 'portrait' : 'landscape'}`}>
            {requestData?.summaryPolyline ? (
              <div className={`progress-map-container ${orientation === 'portrait' ? 'portrait' : 'landscape'}`}>
                <ProgressMap
                  polyline={requestData.summaryPolyline}
                  progressPercentage={progressPercentage}
                  activityType={post?.activityType}
                />
                <div className="progress-text-overlay">
                  <h3>{t('managePost.creatingYourMythicMap', 'Creating Your Mythic Map')}</h3>
                  <p className="progress-message">
                    {getStatusMessage()}
                  </p>
                  <div className="progress-bar-container">
                    <div className="progress-bar">
                      <div className={`progress-bar-fill${progressPercentage >= 100 ? ' complete' : ''}`} style={{ width: `${Math.min(progressPercentage, 100)}%` }}></div>
                    </div>
                    <div className="progress-info">
                      <span className="progress-percentage">{Math.round(progressPercentage)}%</span>
                      {etaText && <span className="progress-eta">{etaText}</span>}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="progress-spinner">
                  <div className="spinner"></div>
                </div>
                <h3>{t('managePost.creatingYourMythicMap', 'Creating Your Mythic Map')}</h3>
                <p className="progress-message">
                  {getStatusMessage()}
                </p>
                <div className="progress-bar-container">
                  <div className="progress-bar">
                    <div className={`progress-bar-fill${progressPercentage >= 100 ? ' complete' : ''}`} style={{ width: `${Math.min(progressPercentage, 100)}%` }}></div>
                  </div>
                  <div className="progress-info">
                    <span className="progress-percentage">{Math.round(progressPercentage)}%</span>
                    {etaText && <span className="progress-eta">{etaText}</span>}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Completed media layer — fades in over the progress overlay */}
        {!isGenerating && media.length > 0 && (
          <div className={`post-image-reveal ${imageRevealed ? 'revealed' : ''}`}>
            <div
              className="post-image-stack"
              onClick={() => {
                if (media.length > 1) {
                  setCurrentImageIndex((previousIndex) => (previousIndex + 1) % media.length);
                }
              }}
            >
              {media.map((item, index) => {
                const isActive = index === currentImageIndex;
                // Portrait class drives video sizing (no-op for <img>, which sizes
                // by natural aspect ratio). Video items carry their own orientation
                // from the backend.
                const isPortrait = item.kind === 'video' ?
                  item.orientation === 'portrait' :
                  post.mapOrientation === 'portrait';
                const layerClass = `post-image stack-layer ${isActive ? 'active' : 'inactive'} ${isPortrait ? 'portrait' : ''}`;
                if (item.kind === 'video') {
                  return (
                    <video
                      key={`${post.id}-vid-${index}`}
                      ref={(el) => { videoRefs.current[index] = el; }}
                      src={item.uri}
                      className={layerClass}
                      playsInline
                      muted
                      loop
                      preload="auto"
                      onDurationChange={(event) => updateVideoProgress(event.currentTarget)}
                      onLoadedMetadata={(event) => updateVideoProgress(event.currentTarget)}
                      onTimeUpdate={(event) => updateVideoProgress(event.currentTarget)}
                    />
                  );
                }
                return (
                  <img
                    key={`${post.id}-img-${index}`}
                    src={item.uri}
                    alt={getImageLabel(item.role)}
                    className={layerClass}
                    loading={index === 0 ? 'eager' : 'auto'}
                    decoding="async"
                    draggable={false}
                  />
                );
              })}
            </div>

            {currentMediaItem?.kind === 'video' && (
              <VideoSlideOverlay
                key={`${post.id}-video-overlay-${currentImageIndex}`}
                label={t('managePost.videoBadge', 'Video')}
                loadingLabel={t('managePost.loadingVideo', 'Loading video')}
                isLoading={!isCurrentVideoReady}
                progressFillRef={progressFillRef}
              />
            )}

            {/* View on Strava link for original map */}
            {currentImageType === 'original' && post.stravaActivityId && (
              <a
                href={`https://www.strava.com/activities/${post.stravaActivityId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="strava-overlay-link"
                onClick={(e) => e.stopPropagation()}
              >
                View on Strava
              </a>
            )}

            {/* Image navigation dots */}
            <div className="image-dots">
              {media.map((item, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                  className={`image-dot ${index === currentImageIndex ? 'active' : ''}`}
                  aria-label={getImageLabel(item.role)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Actions and Stats */}
      <div className="post-actions">
        {/* Caption with Activity Icon and Stats */}
        <div className="post-caption">
          <span className="caption-text">
            {post.caption ? (
              <>
                {post.caption}
                {requestData?.stravaStats && (
                  <>
                    {' '}
                    {requestData.stravaStats.distance && `Distance: ${(requestData.stravaStats.distance / 1000).toFixed(1)} km`}
                    {requestData.stravaStats.movingTime && ` | Time: ${formatDuration(requestData.stravaStats.movingTime)}`}
                    {requestData.stravaStats.averageWatts && ` | Power: ${Math.round(requestData.stravaStats.averageWatts)} W`}
                    {requestData.stravaStats.elevationGain && ` | Elevation: ${Math.round(requestData.stravaStats.elevationGain)} m`}
                  </>
                )}
              </>
            ) : isGenerating ? (
              <span className="caption-loading-shimmer" />
            ) : null}
          </span>
          {post.activityType && (
            <div className="activity-icon">
              {(post.activityType === 'ride' || post.activityType === 'virtualride') && '🚴'}
              {post.activityType === 'run' && '🏃'}
              {post.activityType === 'walk' && '🚶'}
              {post.activityType === 'hike' && '🥾'}
              {post.activityType === 'swim' && '🏊'}
              {post.activityType === 'sailing' && '⛵'}
              {post.activityType === 'windsurfing' && '🏄'}
              {post.activityType === 'kayak' && '🛶'}
            </div>
          )}
        </div>

        {/* Edit Section */}
        <div className="edit-section">
          <div className="edit-actions-horizontal">
            <button className="edit-btn-inline" onClick={handleTryAgain} disabled={isTryingAgain || isGenerating}>
              <IconRefresh size={16} />
              <span>{isTryingAgain ? t('managePost.regenerating', 'Regenerating...') : t('managePost.tryAgain', 'Try Again')}</span>
            </button>
          </div>
        </div>
        
        {/* Save and Publish Section */}
        <div className="publish-section">
          <h4>{t('managePost.share', 'Share')}</h4>
          <div className="publish-actions-horizontal">
            <button className={`publish-btn primary${isPublished ? ' published' : ''}`} onClick={() => setShowPublishModal(true)} disabled={isPublished || isGenerating}>
              {isPublished ? t('managePost.published', 'Published ✓') : t('managePost.publishAndEarn', 'Publish & Earn 💎')}
            </button>
            <button className="publish-btn" onClick={() => setShowShareModal(true)} disabled={isGenerating}>
              <IconShare size={18} />
              {t('managePost.share', 'Share')}
            </button>
          </div>
          
          {/* Strava Share - Separate row for consistent layout */}
          {post.stravaActivityId && (
            <div className="strava-share-section">
              <button
                className={`publish-btn strava full-width${directStravaShareUiState === 'success' || optimisticStravaCreditClaimed || (stravaShareInitiated && !hasClaimedWatermarkCredit) ? ' success' : ''}`}
                onClick={handleStravaShare}
                disabled={isDownloading || isGenerating || isCheckingWatermark}
              >
                {renderStravaShareButtonIcon()}
                {getStravaShareButtonLabel()}
              </button>
            </div>
          )}
        </div>

        {/* Restyle upsell for free render-only Zwift maps */}
        {requestData?.renderOnly === true && post.stravaActivityId && !isGenerating && (
          <div className="restyle-upsell-section">
            <div className="restyle-upsell-info">
              <h4>{t('managePost.restyleTitle', '✨ Turn this ride into art')}</h4>
              <p>{t('managePost.restyleDesc', 'This is your free Zwift map. Generate a Mythic style version of the same ride with AI.')}</p>
            </div>
            <button
              className="publish-btn primary full-width"
              onClick={() => {
                // Restyle from the map the user is looking at: the primary
                // generated map keeps its dimension (3D for Watopia duals),
                // anything else — the 2D map, the Strava original — goes 2D.
                const restyleDimension =
                  currentMediaItem?.role === 'generated' && requestData?.use3DMap === true ? '3d' : '2d';
                navigate(`/create/${post.stravaActivityId}?restyle=1&dim=${restyleDimension}`);
              }}
            >
              <IconSparkles size={18} />
              {t('managePost.restyleCta', 'Restyle in a Mythic style')}
            </button>
          </div>
        )}

        {/* Delete Action */}
        <div className="manage-actions">
          <button className="manage-action-btn delete" onClick={onDelete} disabled={isGenerating}>
            <IconTrash size={18} />
            {t('managePost.deletePost', 'Delete Post')}
          </button>
        </div>
      </div>

      {/* Edit Stats Panel Modal */}
      {showEditStatsModal && (
        <div className="modal-overlay" onClick={() => setShowEditStatsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t('managePost.editStatsPanel', 'Edit Stats Panel')} <IconDiamond size={20} style={{ color: '#8b9dc3', verticalAlign: 'middle' }} /></h3>
            <p style={{ fontSize: '14px', color: '#6b6b6b', marginBottom: '16px' }}>
              {t('managePost.editStatsPanelDesc', 'Tell the AI what text you want in the stats panel. This costs 1 diamond.')}
            </p>
            <textarea
              value={editedStatsText}
              onChange={(e) => setEditedStatsText(e.target.value)}
              className="edit-textarea"
              placeholder={t('managePost.editStatsPanelPlaceholder', "e.g., 'Morning Run - Distance: 51.0 km | Time: 2h 57m | Elevation: 331 m'")}
              rows={4}
            />
            <div className="modal-actions">
              <button onClick={() => setShowEditStatsModal(false)} className="cancel-button">
                {t('common.cancel', 'Cancel')}
              </button>
              <button onClick={handleEditStatsPanel} className="save-button diamond">
                <IconDiamond size={16} />
                {t('managePost.regenerate', 'Regenerate')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Confirmation Modal */}
      {showPublishModal && (
        <div className="modal-overlay" onClick={() => !isPublishing && !publishSuccess && setShowPublishModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {!publishSuccess ? (
              <>
                <h3>{t('managePost.publishToFeed', 'Publish to Feed & Earn 💎')}</h3>
                <p style={{ fontSize: '14px', color: '#6b6b6b', marginBottom: '20px' }}>
                  {t('managePost.publishToFeedDesc', 'Share your map with the Mythic Maps community and earn 1 diamond!')}
                </p>
                <div className="modal-actions">
                  <button onClick={() => setShowPublishModal(false)} className="cancel-button" disabled={isPublishing}>
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button onClick={handlePublish} className="save-button" disabled={isPublishing}>
                    {isPublishing ? (
                      <>
                        <div className="spinner-small"></div>
                        {t('managePost.publishing', 'Publishing...')}
                      </>
                    ) : (
                      t('managePost.publishNow', 'Publish Now')
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Success State */}
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                  <h3 style={{ color: '#10b981', marginBottom: '12px' }}>
                    {t('managePost.publishedSuccessTitle', 'Published Successfully!')}
                  </h3>
                  {publishEarnedDiamond && (
                    <div style={{ 
                      background: '#f0fdf4', 
                      border: '1px solid #86efac', 
                      borderRadius: '8px', 
                      padding: '16px',
                      marginBottom: '20px'
                    }}>
                      <p style={{ fontSize: '16px', color: '#16a34a', margin: 0, fontWeight: 600 }}>
                        🎉 {t('managePost.earnedDiamond', 'You earned 1 diamond 💎')}
                      </p>
                    </div>
                  )}
                  <p style={{ fontSize: '14px', color: '#6b6b6b', marginBottom: '24px' }}>
                    {t('managePost.publishedSuccessDesc', 'Your map is now visible in the public feed!')}
                  </p>
                </div>
                <div className="modal-actions">
                  <button 
                    onClick={() => {
                      setShowPublishModal(false);
                      setPublishSuccess(false);
                      setPublishEarnedDiamond(false);
                    }} 
                    className="save-button"
                    style={{ width: '100%' }}
                  >
                    {t('common.ok', 'OK')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t('managePost.shareYourMap', 'Share Your Map')}</h3>

            {/* Per-item selection: applies to Download (and Instagram/TikTok which
                trigger the same download). Link-based shares are unaffected — they
                always link to /post/<id> which shows the full media set. */}
            <MediaSelectionTiles
              tiles={[
                { key: 'generated', item: generatedMediaItem, label: t('managePost.shareItemGenerated', 'Generated map') },
                { key: 'video', item: videoMediaItem, label: t('managePost.shareItemVideo', 'Transition video') },
                { key: 'original', item: originalMediaItem, label: t('managePost.shareItemOriginal', 'Original map') },
              ]}
              selection={shareSelection}
              onToggle={toggleShareSelection}
              label={t('managePost.shareSelectionLabel', 'Pick what you want to share')}
            />
            {!hasShareSelection && (
              <p className="share-selection-hint">
                {t('managePost.shareSelectionEmpty', 'Pick at least one item to download.')}
              </p>
            )}

            {/* Download and Copy Link Options */}
            <div className="share-primary-actions">
              <button className="share-primary-btn" onClick={handleDownload} disabled={isDownloading || !hasShareSelection}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                {isDownloading ?
                  t('managePost.downloading', 'Downloading...') :
                  (selectedShareCount > 1 ?
                    t('managePost.downloadMultiple', 'Download {{count}} files', { count: selectedShareCount }) :
                    t('managePost.downloadImage', 'Download'))}
              </button>
              <button className="share-primary-btn" onClick={handleCopyLink}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                </svg>
                {t('managePost.copyLink', 'Copy Link')}
              </button>
            </div>
            
            <div className="share-divider">
              <span>{t('managePost.shareOnSocialMedia', 'Share on social media')}</span>
            </div>
            
            <div className="share-grid">
              <button className="share-option twitter" onClick={() => handleShare('twitter')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                <span>{t('managePost.twitter', 'Twitter')}</span>
              </button>
              <button className="share-option facebook" onClick={() => handleShare('facebook')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                <span>{t('managePost.facebook', 'Facebook')}</span>
              </button>
              <button className="share-option instagram" onClick={() => handleShare('instagram')} disabled={!hasShareSelection}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
                </svg>
                <span>{t('managePost.instagram', 'Instagram')}</span>
              </button>
              <button className="share-option tiktok" onClick={() => handleShare('tiktok')} disabled={!hasShareSelection}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                </svg>
                <span>{t('managePost.tiktok', 'TikTok')}</span>
              </button>
              <button className="share-option reddit" onClick={() => handleShare('reddit')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
                </svg>
                <span>{t('managePost.reddit', 'Reddit')}</span>
              </button>
            </div>
            
            <div className="modal-actions">
              <button onClick={() => setShowShareModal(false)} className="cancel-button">
                {t('managePost.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Strava Share Modal — picker for which media items to upload to Strava */}
      {showStravaShareModal && (() => {
        const stravaSelectedCount = (stravaShareSelection.generated && hasGeneratedMedia ? 1 : 0) +
          (stravaShareSelection.generated2d && hasGenerated2dMedia ? 1 : 0) +
          (stravaShareSelection.video && hasStravaTransitionVideo ? 1 : 0) +
          (stravaShareSelection.original && hasOriginalMedia && !isRenderOnlyPost ? 1 : 0);
        const hasStravaSelection = stravaSelectedCount > 0;
        const isUploading = directStravaShareUiState === 'sharing' || isDownloading;
        const autoUploadToggleDisabled = isAutoUploadPreferenceLoading ||
          isAutoUploadPreferenceSaving ||
          (!autoUploadEnabled && !autoUploadHasProfileScope);
        const shouldShowAutoUploadToggle = user?.uid &&
          hasLoadedAutoUploadPreference &&
          shouldShowAutoUploadToggleForModal;
        const closeModal = () => {
          if (isUploading) return;
          setShowStravaShareModal(false);
        };
        const confirm = async () => {
          if (!hasStravaSelection || isUploading) return;
          setShowStravaShareModal(false);
          await runDirectStravaUpload(stravaShareSelection);
        };
        return (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>{t('managePost.shareToStravaTitle', 'Share to Strava')}</h3>
              <MediaSelectionTiles
                tiles={isRenderOnlyPost ? [
                  {
                    key: 'generated',
                    item: generatedMediaItem,
                    label: requestData?.use3DMap === true
                      ? t('managePost.shareItem3dMap', '3D map')
                      : t('managePost.shareItem2dMap', '2D map'),
                  },
                  ...(generated2dMediaItem ? [{
                    key: 'generated2d',
                    item: generated2dMediaItem,
                    label: t('managePost.shareItem2dMap', '2D map'),
                  }] : []),
                ] : [
                  { key: 'generated', item: generatedMediaItem, label: t('managePost.shareItemGenerated', 'Generated map') },
                  { key: 'video', item: stravaTransitionVideoItem, label: t('managePost.shareItemVideo', 'Transition video') },
                  { key: 'original', item: originalMediaItem, label: t('managePost.shareItemOriginal', 'Original map') },
                ]}
                selection={stravaShareSelection}
                onToggle={toggleStravaShareSelection}
                label={t('managePost.shareToStravaPickerLabel', 'Pick what to upload to your Strava activity:')}
              />
              {shouldShowAutoUploadToggle && (
                <div className="strava-auto-upload-toggle-card">
                  <div className="strava-auto-upload-toggle-copy">
                    <div className="strava-auto-upload-toggle-title-row">
                      <span className="strava-auto-upload-toggle-title">
                        {t('managePost.autoUploadToggleTitle', 'Auto-upload future activities')}
                      </span>
                      {isAutoUploadPreferenceSaving && (
                        <span className="strava-auto-upload-toggle-saving">
                          {t('managePost.saving', 'Saving...')}
                        </span>
                      )}
                    </div>
                    <p>
                      {autoUploadHasProfileScope
                        ? t('managePost.autoUploadToggleDesc', 'Automatically make and share maps for new Strava activities, free every time.')
                        : t('managePost.autoUploadToggleScopeDesc', 'Reconnect Strava in settings to enable future auto-uploads.')}
                    </p>
                    {!autoUploadHasProfileScope && (
                      <button
                        type="button"
                        className="strava-auto-upload-settings-link"
                        onClick={() => navigate('/settings#auto-upload')}
                      >
                        {t('managePost.autoUploadReconnect', 'Open auto-upload settings')}
                      </button>
                    )}
                  </div>
                  <label className="toggle-switch strava-auto-upload-switch">
                    <input
                      type="checkbox"
                      checked={autoUploadEnabled}
                      onChange={handleAutoUploadToggle}
                      disabled={autoUploadToggleDisabled}
                      aria-label={t('managePost.autoUploadToggleTitle', 'Auto-upload future activities')}
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>
              )}
              {!hasStravaSelection && (
                <p className="share-selection-hint">
                  {t('managePost.shareToStravaSelectionEmpty', 'Pick at least one item to share.')}
                </p>
              )}
              <div className="modal-actions">
                <button onClick={closeModal} className="cancel-button" disabled={isUploading}>
                  {t('managePost.cancel', 'Cancel')}
                </button>
                <button
                  onClick={confirm}
                  className="save-button"
                  disabled={!hasStravaSelection || isUploading}
                >
                  {isUploading ?
                    t('managePost.sharingToStrava', 'Sharing to Strava...') :
                    shouldSkipWatermarkCreditCheck ?
                      t('managePost.shareToStravaFree', 'Share to Strava') :
                      t('managePost.shareToStrava', 'Share to Strava & Earn 💎')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showStravaShareErrorModal && (
        <div className="modal-overlay" onClick={() => setShowStravaShareErrorModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
              <h3 style={{ color: '#dc2626', marginBottom: '12px' }}>
                {t('managePost.stravaUploadErrorTitle', 'Strava Upload Error')}
              </h3>
              <p style={{ fontSize: '14px', color: '#6b6b6b', marginBottom: '20px', lineHeight: 1.6 }}>
                {stravaShareErrorMessage}
              </p>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setShowStravaShareErrorModal(false)}
                className="save-button"
                style={{ width: '100%' }}
              >
                {t('common.ok', 'OK')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Watermark Credit Success Modal */}
      {showWatermarkSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowWatermarkSuccessModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
              <h3 style={{ color: '#10b981', marginBottom: '12px' }}>
                {t('managePost.mapFoundTitle', 'We found your Map!')}
              </h3>
              <div style={{
                background: '#f0fdf4',
                border: '1px solid #86efac',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <p style={{ fontSize: '16px', color: '#16a34a', margin: 0, fontWeight: 600 }}>
                  🎉 {t('managePost.earnedDiamond', 'You earned 1 diamond 💎')}
                </p>
              </div>
              <p style={{ fontSize: '14px', color: '#6b6b6b', marginBottom: '24px' }}>
                {t('managePost.thankYouForSharing', 'Thank you for sharing your mythic map on Strava!')}
              </p>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setShowWatermarkSuccessModal(false)}
                className="save-button"
                style={{ width: '100%' }}
              >
                {t('common.ok', 'OK')}
              </button>
            </div>
          </div>
        </div>
      )}

    </article>
  );
};

export default ManagePostCard;
